import { useEffect, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { X, Globe, Activity, RefreshCw } from 'lucide-react';
import StatusPill from '../common/StatusPill';
import TimeRangeSelector from '../common/TimeRangeSelector';
import { fetchWebsiteLatencyHistory } from '../../api/api';
import { formatDate } from '../../fonctions/fonctions';

function mapWStatus(status) {
  const s = (status || '').toLowerCase();
  if (s === 'inaccessible' || s === 'hors ligne') return 'down';
  if (s === 'ralenti' || s === 'dégradé')         return 'degraded';
  if (s === 'opérationnel')                        return 'operational';
  return 'unknown';
}

function parseLatencyMs(latenceStr) {
  if (!latenceStr) return null;
  const n = parseFloat(latenceStr.replace('ms', ''));
  return isNaN(n) ? null : n;
}

function axisTime(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function statusColor(idStatus) {
  if (idStatus === 3) return '#dc2626';
  if (idStatus === 2) return '#ea580c';
  return '#16a34a';
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const color = statusColor(payload[0]?.payload?.id_status);
  return (
    <div style={{
      background: 'white', border: '1px solid #e5e7eb',
      borderRadius: 8, padding: '7px 12px', fontSize: '0.78rem',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div style={{ color: '#9ca3af', marginBottom: 3 }}>
        {new Date(label).toLocaleString('fr-FR')}
      </div>
      <div style={{ color, fontWeight: 700 }}>{payload[0].value?.toFixed(1)} ms</div>
    </div>
  );
}

function LatencyChart({ title, data, gradId }) {
  if (!data || data.length === 0) return null;

  const points = data
    .map(r => ({ time: r.created_date, value: parseLatencyMs(r.latence), id_status: r.id_status }))
    .filter(p => p.value !== null);

  if (points.length === 0) return null;

  const lastStatus = points[points.length - 1]?.id_status || 1;
  const color      = statusColor(lastStatus);

  return (
    <div style={{
      background: 'white', border: '1px solid var(--border-md)',
      borderRadius: 'var(--radius-lg)', padding: '1.1rem 1.25rem',
      boxShadow: 'var(--shadow-xs)',
    }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.85rem' }}>
        {title}
      </div>
      <ResponsiveContainer width="100%" height={150}>
        <AreaChart data={points} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.18} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="time" tickFormatter={axisTime} tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} unit="ms" />
          <Tooltip content={<ChartTooltip />} />
          <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#${gradId})`} dot={false} activeDot={{ r: 4, fill: color, strokeWidth: 0 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function WebsiteDrawer({ website, onClose }) {
  const [history,  setHistory]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [visible,  setVisible]  = useState(false);
  const [hours,    setHours]    = useState(24);

  const isOpen = !!website;

  useEffect(() => {
    if (isOpen) requestAnimationFrame(() => setVisible(true));
    else         setVisible(false);
  }, [isOpen]);

  useEffect(() => {
    if (!website) return;
    setHistory(null);
    setLoading(true);
    fetchWebsiteLatencyHistory(website.client_id, hours)
      .then(r => { if (r.success) setHistory(r.data); })
      .catch(err => console.error('Erreur historique website:', err))
      .finally(() => setLoading(false));
  }, [website, hours]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  if (!isOpen && !visible) return null;

  const siteStatus = mapWStatus(website?.website_status);
  const apiStatus  = website?.api ? mapWStatus(website.api.api_status) : null;

  return (
    <>
      <div
        style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.28)', backdropFilter:'blur(3px)', zIndex:300, opacity: visible?1:0, transition:'opacity 0.25s ease' }}
        onClick={onClose}
      />

      <aside style={{
        position:'fixed', top:0, right:0, bottom:0, width:620, maxWidth:'95vw',
        background:'#f8fafc', borderLeft:'1px solid var(--border-md)',
        boxShadow:'-8px 0 40px rgba(0,0,0,0.12)',
        display:'flex', flexDirection:'column',
        transform: visible ? 'translateX(0)' : 'translateX(100%)',
        transition:'transform 0.28s cubic-bezier(0.22,1,0.36,1)',
        zIndex:301, overflow:'hidden',
      }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1.25rem 1.5rem', background:'white', borderBottom:'1px solid var(--border-md)', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:40, height:40, borderRadius:'var(--radius-md)', background:'var(--accent-dim)', border:'1px solid var(--accent-glow)', color:'var(--accent-light)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <Globe size={18} />
            </div>
            <div>
              <div style={{ fontSize:'1.05rem', fontWeight:800, color:'var(--text)' }}>{website?.client_name}</div>
              <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:2 }}>
                Màj {formatDate(website?.website_last_update)}
              </div>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <StatusPill status={siteStatus} label={website?.website_status} />
            <button onClick={onClose} style={{ display:'flex', alignItems:'center', justifyContent:'center', width:32, height:32, borderRadius:'var(--radius-sm)', border:'1px solid var(--border-md)', background:'#f9fafb', color:'var(--text-muted)', cursor:'pointer', transition:'all 0.16s ease' }}
              onMouseEnter={e=>{e.currentTarget.style.background='#fee2e2';e.currentTarget.style.color='#dc2626';}}
              onMouseLeave={e=>{e.currentTarget.style.background='#f9fafb';e.currentTarget.style.color='var(--text-muted)';}}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'1.5rem', display:'flex', flexDirection:'column', gap:'1.25rem' }}>

          {/* Statut actuel */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.75rem' }}>
            <div style={{ background:'white', border:'1px solid var(--border-md)', borderRadius:'var(--radius-md)', padding:'1rem' }}>
              <div style={{ fontSize:'0.65rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:8 }}>Site web</div>
              <StatusPill status={siteStatus} label={website?.website_status} />
              {website?.website_latency && (
                <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:6, fontFamily:'monospace' }}>
                  Latence : {website.website_latency}
                  {website.avg_latency && website.avg_latency !== 'N/A' && ` · Moy : ${website.avg_latency}`}
                </div>
              )}
            </div>

            {website?.api && (
              <div style={{ background:'white', border:'1px solid var(--border-md)', borderRadius:'var(--radius-md)', padding:'1rem' }}>
                <div style={{ fontSize:'0.65rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:8 }}>API</div>
                <StatusPill status={apiStatus} label={website.api.api_status} />
                {website.api.api_latency && (
                  <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:6, fontFamily:'monospace' }}>
                    Latence : {website.api.api_latency}
                    {website.api.avg_latency && website.api.avg_latency !== 'N/A' && ` · Moy : ${website.api.avg_latency}`}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Historique */}
          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.85rem' }}>
              <div style={{ fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', display:'flex', alignItems:'center', gap:6 }}>
                <Activity size={13} style={{ color:'var(--accent-light)' }} />
                Historique de latence
              </div>
              <TimeRangeSelector value={hours} onChange={setHours} />
            </div>

            {loading && (
              <div style={{ display:'flex', alignItems:'center', gap:8, color:'var(--text-secondary)', fontSize:'0.85rem' }}>
                <RefreshCw size={14} className="spin" /> Chargement…
              </div>
            )}

            {!loading && history && history.websiteHistory.length === 0 && history.apiHistory.length === 0 && (
              <div style={{ padding:'2rem', textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem', background:'white', border:'1px solid var(--border-md)', borderRadius:'var(--radius-lg)' }}>
                Aucune donnée historique disponible.<br />
                Les graphiques s'alimenteront toutes les 2 minutes.
              </div>
            )}

            {!loading && history && (
              <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
                <LatencyChart title="Latence site web" data={history.websiteHistory} gradId="grad-site" />
                <LatencyChart title="Latence API" data={history.apiHistory} gradId="grad-api" />
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

export default WebsiteDrawer;
